/**
 * Страница журнала аудита.
 * Доступна только врачу. Показывает историю действий пользователей.
 */

import React, { useEffect, useState } from 'react';
import { doctorAPI } from '../../api/client';

interface AuditEntry {
  id: number;
  user_id: number | null;
  user_name: string | null;
  user_role: string | null;
  action: string;
  details: string | null;
  ip_address: string | null;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  login: 'Вход в систему',
  register: 'Регистрация',
  create_report: 'Создание записи',
  update_report: 'Обновление записи',
  delete_report: 'Удаление записи',
  change_password: 'Смена пароля',
  submit_test: 'Прохождение теста',
};

const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 30;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (offset = 0) => {
    try {
      const res = await doctorAPI.getAuditLog({ limit: PAGE_SIZE, offset });
      if (offset === 0) {
        setEntries(res.data.entries);
      } else {
        setEntries((prev) => [...prev, ...res.data.entries]);
      }
      setTotal(res.data.total);
    } catch (err) {
      console.error('Ошибка загрузки аудита:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p>Загрузка...</p>;

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Журнал аудита</h1>
      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        Всего записей: {total}
      </p>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Дата/время</th>
              <th style={styles.th}>Пользователь</th>
              <th style={styles.th}>Роль</th>
              <th style={styles.th}>Действие</th>
              <th style={styles.th}>Детали</th>
              <th style={styles.th}>IP</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td style={styles.td}>
                  {e.created_at ? new Date(e.created_at).toLocaleString('ru-RU') : '—'}
                </td>
                <td style={styles.td}>{e.user_name || '—'}</td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.roleBadge,
                    backgroundColor: e.user_role === 'doctor' ? '#eff6ff' : '#f0fdf4',
                    color: e.user_role === 'doctor' ? '#2563eb' : '#16a34a',
                  }}>
                    {e.user_role === 'doctor' ? 'Врач' : e.user_role === 'patient' ? 'Пациент' : '—'}
                  </span>
                </td>
                <td style={styles.td}>{ACTION_LABELS[e.action] || e.action}</td>
                <td style={{ ...styles.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.details || '—'}
                </td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{e.ip_address || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {entries.length < total && (
          <button
            onClick={() => loadData(entries.length)}
            style={styles.loadMore}
          >
            Загрузить ещё ({total - entries.length})
          </button>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    backgroundColor: 'var(--bg-table-header)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-color)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    fontSize: 13,
    borderBottom: '1px solid var(--border-light)',
  },
  roleBadge: {
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  loadMore: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'var(--bg-btn-secondary)',
    color: 'var(--text-btn-secondary)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    marginTop: 12,
  },
};

export default AuditLogPage;
